/**
 * ModalCoverPicker.jsx
 * Модальное окно выбора обложки (десктоп).
 * Загружает шаблоны с сервера, поиск по названию/тегам, превью выбранной обложки.
 * После подтверждения вызывает onConfirm(coverTemplate) и отправляет PATCH на сервер.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Box from "@mui/joy/Box";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import IconButton from "@mui/joy/IconButton";
import Input from "@mui/joy/Input";
import Chip from "@mui/joy/Chip";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalOverflow from "@mui/joy/ModalOverflow";
import ModalClose from "@mui/joy/ModalClose";
import CircularProgress from "@mui/joy/CircularProgress";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";

import { bookApi } from "../../api.js";
import { SupportButtons } from "../BottomSheet/BottomSheetCoverPicker.jsx";
import { Grid } from "@mui/joy";


// ─── Популярные теги для быстрого фильтра ────────────────────────────────────

const QUICK_TAGS = [
  "все", "россия", "мир", "предметы", "детские", "свадьба любовь"
];


// ─── Карточка одной обложки ───────────────────────────────────────────────────

function CoverCard({ cover, selected, excluded, onClick }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <Grid
      onClick={() => onClick(cover)}
      xs={4} sm={2}
      sx={{
        position: "relative",
        cursor: "pointer",
        aspectRatio: "3/4",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: "16px",
        p: 1.5,
        m: 0.5,
        bgcolor: selected
          ? "color-mix(in srgb, var(--joy-palette-primary-500) 8%, var(--joy-palette-background-surface))"
          : "background.surface",
        border: selected
          ? "2.5px solid var(--joy-palette-primary-400)"
          : "2.5px solid var(--joy-palette-divider)",
        transition: "all 0.18s cubic-bezier(0.4,0,0.2,1)",
        "&:hover": {
          transform: "translateY(-4px)",
          borderColor: selected
            ? "var(--joy-palette-primary-400)"
            : "color-mix(in srgb, var(--joy-palette-primary-400) 50%, transparent)",
          "& .sticker-img": {
            filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.25)) drop-shadow(0 2px 4px rgba(0,0,0,0.15))",
            transform: "scale(1.05)",
          },
        },
        "&:active": {
          transform: "scale(0.97)",
        },
        ...(excluded && {
          opacity: 0.4,
          filter: "grayscale(0.6)",
          pointerEvents: "auto", // клик всё ещё работает — для показа предупреждения
        }),
      }}
    >
      {/* Стикер */}
      {cover.image_url ? (
        <Box
          component="img"
          className="sticker-img"
          src={cover.thumbnail_url || cover.image_url}
          alt={cover.title}
          onLoad={() => setLoaded(true)}
          draggable={false}
          sx={{
            width: "85%",
            height: "auto",
            maxHeight: "75%",
            objectFit: "contain",
            display: "block",
            my: 'auto',
            userSelect: "none",
            opacity: loaded ? 1 : 0,
            filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.18)) drop-shadow(0 1px 3px rgba(0,0,0,0.12))",
            transition: "filter 0.25s, transform 0.25s, opacity 0.3s",
          }}
        />
      ) : (
        <Box sx={{
          width: "100%", height: "100%",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <ImageRoundedIcon sx={{ fontSize: 32, color: "neutral.400" }} />
        </Box>
      )}

      {/* Скелетон */}
      {!loaded && cover.image_url && (
        <Box sx={{
          position: "absolute", inset: 0,
          borderRadius: "16px",
          bgcolor: "neutral.100",
          animation: "shimmer 1.4s ease infinite",
          "@keyframes shimmer": {
            "0%, 100%": { opacity: 0.6 },
            "50%": { opacity: 1 },
          },
        }} />
      )}

      {/* Название внизу */}
      {/* <Typography sx={{
        fontSize: "12px",
        fontWeight: 600,
        color: "text.secondary",
        pt: 1,
        textAlign: "center",
        lineHeight: 1.3,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        width: "100%",
      }}>
        {cover.title}
      </Typography> */}

      {/* Галочка при выборе */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 28 }}
            style={{
              position: "absolute", top: 8, right: 8,
              width: 24, height: 24,
              borderRadius: "50%",
              background: "var(--joy-palette-primary-500)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 2px 8px rgba(43,92,230,0.45)",
            }}
          >
            <CheckRoundedIcon sx={{ fontSize: 14, color: "white" }} />
          </motion.div>
        )}
      </AnimatePresence>
    </Grid>
  );
}


// ─── Главный компонент ────────────────────────────────────────────────────────

export default function ModalCoverPicker({ open, onClose, onConfirm, bookId, coverBgColor }) {
  const [covers, setCovers]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState("");
  const [activeTag, setActiveTag] = useState("все");
  const [selected, setSelected]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [warningCover, setWarningCover] = useState(null); // стикер для модалки предупреждения

  const searchTimerRef = useRef(null);

  // Проверка: стикер несовместим с текущим цветом фона?
  const isExcluded = (cover) => {
    if (!coverBgColor || !cover.excluded_bg_colors?.length) return false;
    return cover.excluded_bg_colors.some(
      c => c.toUpperCase() === coverBgColor.toUpperCase()
    );
  };

  // ── Загрузка обложек ──────────────────────────────────────────────────────
  const fetchCovers = useCallback(async (query = "") => {
    setLoading(true);
    try {
      const tag = activeTag !== "все" ? activeTag : "";
      // Если есть поисковый запрос — ищем по нему, иначе по тегу
      const data = await bookApi.getCoverTemplates(query || tag);
      setCovers(data);
    } catch (err) {
      console.error("Ошибка загрузки обложек:", err.message);
    } finally {
      setLoading(false);
    }
  }, [activeTag]);

  useEffect(() => {
    if (open) {
      setSelected(null);
      setSearch("");
      setActiveTag("все");
      fetchCovers();
    }
  }, [open]);

  // Перезагружаем при смене тега
  useEffect(() => {
    if (open) fetchCovers(search);
  }, [activeTag]);

  // Debounce поиска
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      fetchCovers(val);
    }, 400);
  };

  // ── Подтверждение выбора ──────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      // Отправляем на сервер
      if (bookId) {
        await bookApi.updateBook(bookId, { coverTemplateId: selected.id });
      }
      onConfirm(selected);
      onClose();
    } catch (err) {
      console.error("Ошибка сохранения обложки:", err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <Modal
            open={open}
            onClose={onClose}
            sx={{ zIndex: 10010 }}
            slotProps={{
              backdrop: {
                component: motion.div,
                initial: { opacity: 0, backdropFilter: "blur(0px)" },
                animate: { opacity: 1, backdropFilter: "blur(8px)" },
                exit:    { opacity: 0, backdropFilter: "blur(0px)" },
                transition: { duration: 0.28, ease: "easeInOut" },
              },
            }}
          >
            <ModalOverflow sx={{ p: 0, maxHeight: "100vh" }}>
              <ModalDialog
                component={motion.div}
                initial={{ opacity: 0, y: -12, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 12, scale: 0.97 }}
                transition={{ duration: 0.22, ease: "easeInOut" }}
                sx={{
                  width: { xs: "95vw", sm: 640, md: 780 },
                  maxHeight: "88vh !important",
                  p: 0,
                  gap: 0,
                  // overflow: "hidden",
                  borderRadius: "20px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* ── Шапка ── */}
                <Box sx={{
                  px: 3, pt: 2.5, pb: 2,
                  borderBottom: "1px solid", borderColor: "divider",
                  flexShrink: 0,
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <AutoAwesomeRoundedIcon sx={{ fontSize: 20, color: "primary.400" }} />
                      <Typography level="title-md" fontWeight={700}>
                        Выбор обложки
                      </Typography>
                    </Stack>
                    <ModalClose sx={{ position: "static" }} />
                  </Stack>

                  {/* Поиск */}
                  <Input
                    value={search}
                    onChange={handleSearchChange}
                    placeholder="Поиск по названию или тегу..."
                    startDecorator={<SearchRoundedIcon sx={{ fontSize: 18, color: "text.tertiary" }} />}
                    size="sm"
                    sx={{ borderRadius: "10px", mb: 1.5 }}
                  />

                  <Typography level="body-xs" sx={{ color: 'neutral.600', fontWeight: 800, lineHeight: 1.1, mb: 1.25 }}>
                    Напишите название города или страны, также название любого предмета или цвета,  например: «розовый», «цветок» или «животные» и т.д.
                  </Typography>

                  {/* Теги */}
                  <Stack direction="row" gap={0.75} flexWrap="wrap">
                    {QUICK_TAGS.map(tag => (
                      <Chip
                        key={tag}
                        size="sm"
                        variant={activeTag === tag ? "solid" : "soft"}
                        color={activeTag === tag ? "primary" : "neutral"}
                        onClick={() => setActiveTag(tag)}
                        sx={{
                          cursor: "pointer",
                          fontSize: "11px",
                          borderRadius: "8px",
                          transition: "all 0.15s",
                          fontWeight: activeTag === tag ? 700 : 400,
                        }}
                      >
                        {tag}
                      </Chip>
                    ))}
                  </Stack>
                </Box>

                {/* ── Сетка обложек ── */}
                <Box sx={{
                  flex: 1,
                  overflowY: "auto",
                  p: 2.5,
                  "&::-webkit-scrollbar": { width: "4px" },
                  "&::-webkit-scrollbar-thumb": {
                    bgcolor: "color-mix(in srgb, var(--joy-palette-primary-400) 28%, transparent)",
                    borderRadius: "4px",
                  },
                }}>
                  {loading ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                      <CircularProgress size="md" />
                      <Typography level="body-sm" color="neutral" mt={2}>
                        Загружаем обложки...
                      </Typography>
                    </Stack>
                  ) : covers.length === 0 ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                      <ImageRoundedIcon sx={{ fontSize: 48, color: "neutral.300", mb: 1.5 }} />
                      <Typography level="body-sm" color="neutral" textAlign="center">
                        Ничего не найдено.<br />Попробуйте другой запрос или тег.
                      </Typography>
                      <SupportButtons />
                    </Stack>
                  ) : (
                    <Grid container columns={{ xs: 4, sm: 8.4 }}>
                      {covers.map(cover => (
                        <CoverCard
                          key={cover.id}
                          cover={cover}
                          selected={selected?.id === cover.id}
                          onClick={(cover) => {
                            if (isExcluded(cover)) {
                              setWarningCover(cover);
                            } else {
                              setSelected(cover);
                            }
                          }}
                        />
                      ))}
                    </Grid>
                  )}
                </Box>

                {/* ── Футер ── */}
                <Box sx={{
                  px: 3, py: 2,
                  borderTop: "1px solid", borderColor: "divider",
                  flexShrink: 0,
                }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    {selected ? (
                      <Stack direction="row" alignItems="center" gap={1.5}>
                        {selected.image_url && (
                          <Box
                            component="img"
                            src={selected.thumbnail_url || selected.image_url}
                            sx={{
                              width: 36, height: 48,
                              objectFit: "cover",
                              borderRadius: "6px",
                              border: "1.5px solid",
                              borderColor: "primary.200",
                            }}
                          />
                        )}
                        <Box>
                          <Typography level="body-xs" color="neutral">Выбрано:</Typography>
                          <Typography level="body-sm" fontWeight={600}>{selected.title}</Typography>
                        </Box>
                      </Stack>
                    ) : (
                      <Typography level="body-sm" color="neutral">
                        Выберите обложку из списка
                      </Typography>
                    )}

                    <Stack direction="row" gap={1}>
                      <Button
                        variant="plain"
                        color="neutral"
                        size="sm"
                        onClick={onClose}
                        sx={{ borderRadius: "10px" }}
                      >
                        Отмена
                      </Button>
                      <Button
                        variant="solid"
                        color="primary"
                        size="sm"
                        disabled={!selected}
                        loading={saving}
                        onClick={handleConfirm}
                        sx={{ borderRadius: "10px", minWidth: 130, fontWeight: 700 }}
                      >
                        Применить обложку
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </ModalDialog>
            </ModalOverflow>
          </Modal>
        )}
      </AnimatePresence>


      {/* Модалка предупреждения о несовместимости */}
      <Modal open={!!warningCover} onClose={() => setWarningCover(null)} sx={{ zIndex: 10020 }}>
        <ModalDialog sx={{ maxWidth: 400, width: '100%', p: 3, borderRadius: "16px", textAlign: "center" }}>
          <Typography level="title-md" fontWeight={700} sx={{ mb: 1 }}>
            Стикер может не сочетаться
          </Typography>
          <Typography level="body-sm" color="neutral" sx={{ mb: 2.5 }}>
            Цвет этого стикера совпадает или очень близок к цвету фона вашей обложки.
            Стикер может сливаться с фоном и нарушить дизайн.
          </Typography>
          <Stack direction="row" gap={1} justifyContent="center">
            <Button
              variant="outlined"
              color="neutral"
              size="sm"
              onClick={() => setWarningCover(null)}
              sx={{ borderRadius: "10px", flex: 1 }}
            >
              Отменить
            </Button>
            <Button
              variant="solid"
              color="primary"
              size="sm"
              onClick={() => {
                setSelected(warningCover);
                setWarningCover(null);
              }}
              sx={{ borderRadius: "10px", flex: 1, fontWeight: 700 }}
            >
              Всё равно выбрать
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </>
  );
}